/**
 * Authentication Hook
 * Manages user authentication state with secure token handling
 */

import { useState, useEffect, createContext, useContext } from 'react';
import { authService, User } from '../services/auth.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (roles: string | string[]) => boolean;
  showDailyReport: boolean;
  setShowDailyReport: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDailyReport, setShowDailyReport] = useState(false);

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (user) {
      // Refresh token every 14 minutes (token expires in 15)
      const interval = setInterval(() => {
        refreshToken();
      }, 14 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [user]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Auth initialization failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, mfaCode?: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login(email, password, mfaCode);

      if (response.requireMFA) {
        setError('MFA_REQUIRED');
        return;
      }

      setUser(response.user);

      // Show daily report modal after successful login
      setShowDailyReport(true);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithTokens = async (accessToken: string, refreshTokenValue: string) => {
    try {
      setLoading(true);
      setError(null);

      // Store tokens
      authService.setTokens(accessToken, refreshTokenValue);

      // Get current user with the new token
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);

      // Show daily report modal after successful login
      setShowDailyReport(true);
    } catch (err: any) {
      setError(err.message || 'SSO login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const refreshToken = async () => {
    try {
      await authService.refreshToken();
    } catch (err) {
      console.error('Token refresh failed:', err);
      // If refresh fails, log out user
      setUser(null);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    // Super admin has all permissions
    if (user.role === 'super_admin' || user.role === 'org_admin') {
      return true;
    }

    return user.permissions.includes(permission);
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;

    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        token: authService.getAccessToken(),
        isAuthenticated: !!user,
        login,
        loginWithTokens,
        logout,
        refreshToken,
        hasPermission,
        hasRole,
        showDailyReport,
        setShowDailyReport,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
